import React from 'react';
import { observer, PropTypes as MobxPropTypes } from 'mobx-react';

import { ScreenStore } from '../../../stores/ScreenStore';
import { ConfigParametersStore } from '../../../stores/ConfigParametersStore';
import { constants } from '../../../config';
import { ProductSummary } from '../product-summary';
import { GlossaryEntry } from '../../../common/glossary-entry';
import { enforceParameters } from '../../../hocs/enforce-parameters';
import styles from './MainBody.css';

const IPropTypes = {
  comparisonData: MobxPropTypes.arrayOrObservableArray.isRequired,
  products: MobxPropTypes.arrayOrObservableArray.isRequired,
};

/*
  Characteristics have value and descriptions. Both can span multiple lines.
  To make everything look consistent, the last value paragraph must be merged with
  the first paragraph of the description. This process is done below.
*/
const renderCharacteristicContent = (values, descriptions, areDifferent) => (
  <React.Fragment>
    {
      values.map((entry, index, list) => (
        <p
          key={entry.getPlainText()}
        >
          <span
            className={
              (
                areDifferent &&
                entry.getPlainText().length > 1 &&
                !ConfigParametersStore.showIntranet
              ) ? styles.underline : ''
            }
          >
            <GlossaryEntry entry={entry} />
          </span>
          {
            index === list.length - 1 &&
            !!descriptions.length &&
            <React.Fragment>
              &nbsp;&nbsp;
              <GlossaryEntry entry={descriptions[0]} />
            </React.Fragment>
          }
        </p>
      ))
    }
    {
      descriptions.map((entry, index) => (
        index === 0 ? null : (
          <p
            key={entry.getPlainText()}
          >
            <GlossaryEntry entry={entry} />
          </p>
        )
      )).filter(Boolean)
    }
  </React.Fragment>
);

const renderCharacteristic = (characteristic, areDifferent) => (
  <tr key={characteristic.id} className={areDifferent ? styles.background : ''}>
    <td className={styles.nameText}>{characteristic.name}</td>
    <td
      className={[styles.valueText, areDifferent ? styles.different : ''].join(' ')}
    >
      {renderCharacteristicContent(
        characteristic.firstValue,
        characteristic.firstDescription,
        areDifferent,
      )}
    </td>
    <td
      className={[styles.valueText, areDifferent ? styles.different : ''].join(' ')}
    >
      {renderCharacteristicContent(
        characteristic.secondValue,
        characteristic.secondDescription,
        areDifferent,
      )}
    </td>
  </tr>
);

const renderCharacteristicMobile = (characteristic, areDifferent) => (
  <React.Fragment key={characteristic.id}>
    <tr>
      <td className={styles.nameTextMobile}>{characteristic.name}</td>
    </tr>
    <tr className={areDifferent ? styles.background : ''}>
      <td
        className={[styles.valueTextMobile, areDifferent ? styles.different : ''].join(' ')}
      >
        {renderCharacteristicContent(
        characteristic.firstValue,
        characteristic.firstDescription,
        areDifferent,
      )}
      </td>
      <td
        className={[styles.valueTextMobile, areDifferent ? styles.different : ''].join(' ')}
      >
        {renderCharacteristicContent(
        characteristic.secondValue,
        characteristic.secondDescription,
        areDifferent,
      )}
      </td>
    </tr>
  </React.Fragment>
);

const joinGlossaryText = glossaryList => glossaryList.reduce((acc, entry) => (
  `${acc}${entry.getPlainText()}`
), '');

const renderSection = (section, mobileVersion) => (
  <div
    key={section.groupName}
    className={styles.sectionContainer}
  >
    <p className={styles.mainTitle}>
      {section.groupName}
    </p>
    <table
      className={styles.table}
    >
      <tbody>
        {
          section.characteristics.map((characteristic) => {
            const firstJoinedText = joinGlossaryText(characteristic.firstValue);
            const secondJoinedText = joinGlossaryText(characteristic.secondValue);
            const firstJoinedDescription = joinGlossaryText(characteristic.firstDescription);
            const secondJoinedDescription = joinGlossaryText(characteristic.secondDescription);
            const firstFullText = `${firstJoinedText}${firstJoinedDescription}`;
            const secondFullText = `${secondJoinedText}${secondJoinedDescription}`;
            const characteristicsAreDifferent = firstFullText !== secondFullText;
            if (mobileVersion) {
              return renderCharacteristicMobile(characteristic, characteristicsAreDifferent);
            }
            return renderCharacteristic(characteristic, characteristicsAreDifferent);
          })
        }
      </tbody>
    </table>
  </div>
);

const MainBody = (props) => {
  const mobileVersion = ScreenStore.screenWidth <= constants.breakpoints.smallMaxWidth;
  return (
    <div className={styles.container}>
      <p className={styles.mainCompareTitle}> Comparar productos </p>
      <table className={styles.table}>
        <tbody>
          <tr>
            {
              !mobileVersion &&
              <td className={styles.blankColumn} />
            }
            {
              props.products.map(product => (
                <td
                  key={product.id}
                  className={styles.product}
                >
                  <ProductSummary
                    mobileVersion={mobileVersion}
                    product={product}
                  />
                </td>
              ))
            }
          </tr>
        </tbody>
      </table>
      {
        props.comparisonData.map(section => renderSection(section, mobileVersion))
      }
    </div>
  );
};

MainBody.propTypes = IPropTypes;

const ObservedMainBody = enforceParameters(observer(MainBody));

export { ObservedMainBody as MainBody };



// WEBPACK FOOTER //
// ./src/pages/compare/main-body/MainBody.jsx